/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import ReactFlow, {
  Node,
  Edge,
  useNodesState,
  useEdgesState,
  Controls,
  MiniMap,
  Background,
  BackgroundVariant,
  ReactFlowProvider,
  useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';

import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card } from '../ui/card';
import { ScrollArea } from '../ui/scroll-area';
import TaxonomyNode from './TaxonomyNode';
import TaxonomyStatsPanel from './TaxonomyStatsPanel';
import TaxonomyLoadingState from './TaxonomyLoadingState';
import TaxonomySignalsList from './TaxonomySignalsList';
import { fetchWithAuth } from '../../utils/api';
import { TaxonomyApiResponse } from '../../types/taxonomy';

// Define nodeTypes outside component to avoid re-creation
const nodeTypes = {
  taxonomy: TaxonomyNode,
};

interface TaxonomyVisualizerProps {
  mode?: 'overview' | 'signal-focused' | 'category-drill-down';
  highlightSignal?: string;
  filterCategory?: string;
  onNodeClick?: (nodeId: string, associatedSignals: string[]) => void;
}

const TaxonomyVisualizerContent: React.FC<TaxonomyVisualizerProps> = ({
  mode = 'overview',
  highlightSignal,
  filterCategory,
  onNodeClick,
}) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);
  const [taxonomyData, setTaxonomyData] = useState<any>(null);
  const [stats, setStats] = useState<any>(null);
  const [signalClassification, setSignalClassification] = useState<any>(null);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [selectedLabelForSignals, setSelectedLabelForSignals] = useState<{id: string, name: string} | null>(null);
  const { fitView } = useReactFlow();
  const modeLabel =
    mode === 'signal-focused' ? 'Signal focused' : mode === 'category-drill-down' ? 'Drill down' : 'Overview';

  // Comment out WebSocket for now to avoid connection issues
  // TODO: Re-enable WebSocket when needed for real-time updates
  // const { subscribe, unsubscribe } = useWebSocket({
  //   onMessage: (data: any) => {
  //     if (data.type === 'taxonomy_detection') {
  //       handleTaxonomyUpdate(data as TaxonomyDetectionUpdate);
  //     }
  //   },
  // });

  // useEffect(() => {
  //   // Subscribe to taxonomy updates
  //   subscribe('taxonomy', 'global');
  //   return () => unsubscribe('taxonomy', 'global');
  // }, [subscribe, unsubscribe]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadTaxonomyData();
  }, [highlightSignal]);  // Reload when signal changes

  const loadTaxonomyData = async () => {
    try {
      setLoading(true);
      // Include signal ID if provided
      const url = highlightSignal 
        ? `/v1/classify/taxonomy?signal_id=${highlightSignal}`
        : '/v1/classify/taxonomy';
      
      const data: TaxonomyApiResponse = await fetchWithAuth(url);
      
      console.log('Taxonomy API Response:', data);
      console.log('Stats data:', data.stats);
      if (highlightSignal && data.signal_classification) {
        console.log('Signal classification:', data.signal_classification);
      }
      
      if (data.success) {
        setTaxonomyData(data.data);
        setStats(data.stats);
        setSignalClassification(data.signal_classification);
        generateNodesAndEdges(data.data, data.stats, data.signal_classification);
      }
    } catch (error) {
      console.error('Failed to load taxonomy data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateNodesAndEdges = (taxonomy: any, statsData: any, signalClassification?: any) => {
    const newNodes: Node[] = [];
    const newEdges: Edge[] = [];
    
    // Create root node at center
    const rootNode: Node = {
      id: 'root',
      type: 'taxonomy',
      position: { x: 0, y: 0 },
      data: {
        label: 'GO Conduct Intelligence',
        type: 'root',
      },
    };
    newNodes.push(rootNode);
    
    // Layout configuration
    const rootRadius = 600; // Distance from root to main categories
    const categoryRadius = 400; // Distance from category to subcategories
    const labelRadius = 250; // Distance from subcategory to labels
    
    // Process dimensions (4 main dimensions)
    taxonomy.dimensions?.forEach((dimension: any, dimensionIndex: number) => {
      // Position main dimensions in quarters around root
      const dimensionAngle = (dimensionIndex / taxonomy.dimensions.length) * 2 * Math.PI - Math.PI / 2;
      const dimensionX = Math.cos(dimensionAngle) * rootRadius;
      const dimensionY = Math.sin(dimensionAngle) * rootRadius;
      
      const dimensionId = `dimension-${dimension.id}`;
      
      // Create dimension node
      const dimensionNode: Node = {
        id: dimensionId,
        type: 'taxonomy',
        position: { x: dimensionX, y: dimensionY },
        data: {
          label: dimension.name,
          type: 'dimension',
          color: '#500091',
          signalCount: highlightSignal 
            ? getDimensionSignalCountForSignal(dimension.id, signalClassification)
            : 0,
        },
      };
      newNodes.push(dimensionNode);
      
      // Edge from root to dimension
      newEdges.push({
        id: `edge-root-${dimensionId}`,
        source: 'root',
        target: dimensionId,
        style: { stroke: '#500091', strokeWidth: 3 },
      });
      
      // Process categories
      const numCategories = dimension.categories?.length || 0;
      const angleSpread = Math.PI / 3; // 60 degree spread for categories
      const startAngle = dimensionAngle - angleSpread / 2;
      
      dimension.categories?.forEach((category: any, catIndex: number) => {
        const categoryId = `cat-${dimension.id}-${category.id}`;
        
        // Position categories in an arc around their parent dimension
        const catAngle = startAngle + (catIndex / Math.max(numCategories - 1, 1)) * angleSpread;
        const catX = dimensionX + Math.cos(catAngle) * categoryRadius;
        const catY = dimensionY + Math.sin(catAngle) * categoryRadius;
        
        // Create category node
        const categoryNode: Node = {
          id: categoryId,
          type: 'taxonomy',
          position: { x: catX, y: catY },
          data: {
            label: category.name,
            type: 'category',
            color: '#924EFF',
            signalCount: highlightSignal 
              ? getCategorySignalCountForSignal(category, dimension.id, signalClassification)
              : getSignalCountForCategory(category.id, statsData),
          },
        };
        newNodes.push(categoryNode);
        
        // Edge from dimension to category
        newEdges.push({
          id: `edge-${dimensionId}-${categoryId}`,
          source: dimensionId,
          target: categoryId,
          style: { stroke: '#924EFF', strokeWidth: 2 },
        });
        
        // Process labels
        const numLabels = category.labels?.length || 0;
        const labelAngleSpread = Math.PI / 4; // 45 degree spread for labels
        const labelStartAngle = catAngle - labelAngleSpread / 2;
        
        category.labels?.forEach((label: any, labelIndex: number) => {
          const labelId = `label-${label.id}`;
          
          // Position labels in an arc around their category
          const labelAngle = labelStartAngle + (labelIndex / Math.max(numLabels - 1, 1)) * labelAngleSpread;
          const labelX = catX + Math.cos(labelAngle) * labelRadius;
          const labelY = catY + Math.sin(labelAngle) * labelRadius;
          
          // Check if label is detected - if we have a signal, only use signal classification
          const signalDetected = isLabelDetectedInSignal(label.id, dimension.id, category.id, signalClassification);
          const statsDetected = isLabelDetected(label.id, statsData);
          const detected = highlightSignal ? signalDetected : (signalDetected || statsDetected);
          const confidence = highlightSignal
            ? getSignalLabelConfidence(label.id, dimension.id, category.id, signalClassification)
            : (signalDetected 
                ? getSignalLabelConfidence(label.id, dimension.id, category.id, signalClassification)
                : getLabelConfidence(label.id, statsData));
          
          const labelNode: Node = {
            id: labelId,
            type: 'taxonomy',
            position: { x: labelX, y: labelY },
            data: {
              label: label.name,
              type: 'label',
              detected,
              confidence,
              description: label.description,
              signalCount: highlightSignal 
                ? (signalDetected ? 1 : 0) 
                : getSignalCountForLabel(label.id, statsData),
            },
          };
          newNodes.push(labelNode);
          
          // Edge from category to label
          newEdges.push({
            id: `edge-${categoryId}-${labelId}`,
            source: categoryId,
            target: labelId,
            style: { 
              stroke: detected ? '#92204D' : '#07A0C3', 
              strokeWidth: detected ? 2 : 1,
              strokeDasharray: detected ? '0' : '5,5',
            },
            animated: detected,
          });
        });
      });
    });
    
    setNodes(newNodes);
    setEdges(newEdges);
    
    // Fit view after nodes are rendered
    setTimeout(() => {
      fitView({ padding: 0.2, maxZoom: 1.2 });
    }, 100);
  };

  const handleNodeClick = useCallback(
    (event: React.MouseEvent, node: Node) => {
      setSelectedNode(node.id);
      
      // If it's a detected label, show signals list
      if (node.data.type === 'label' && node.data.detected) {
        setSelectedLabelForSignals({
          id: node.id,
          name: node.data.label
        });
      } else {
        setSelectedLabelForSignals(null);
      }
      
      if (onNodeClick && node.data.type === 'label') {
        onNodeClick(node.id, []);
      }
    },
    [onNodeClick]
  );

  // Helper functions
  const isLabelDetectedInSignal = (labelId: string, dimensionId: string, categoryId: string, signalClassification: any) => {
    if (!signalClassification?.dimensions) return false;
    
    const dimension = signalClassification.dimensions[dimensionId];
    if (!dimension?.categories) return false;
    
    const category = dimension.categories[categoryId];
    if (!category?.labels) return false;
    
    return labelId in category.labels;
  };
  
  const getSignalLabelConfidence = (labelId: string, dimensionId: string, categoryId: string, signalClassification: any) => {
    if (!signalClassification?.dimensions) return 0;
    
    const label = signalClassification.dimensions[dimensionId]?.categories?.[categoryId]?.labels?.[labelId];
    return label?.confidence || 0;
  };
  
  const isLabelDetected = (labelId: string, statsData: any) => {
    // The backend returns label_id for the taxonomy label
    return statsData?.top_detected_categories?.some((cat: any) => cat.label_id === labelId) || false;
  };

  const getLabelConfidence = (labelId: string, statsData: any) => {
    const category = statsData?.top_detected_categories?.find((cat: any) => cat.label_id === labelId);
    return category?.average_confidence || 0;
  };

  const getSignalCountForLabel = (labelId: string, statsData: any) => {
    const category = statsData?.top_detected_categories?.find((cat: any) => cat.label_id === labelId);
    return category?.count || 0;
  };

  const getSignalCountForCategory = (categoryId: string, statsData: any) => {
    // This would need to aggregate counts from all items in the category
    return 0;
  };

  const getCategorySignalCountForSignal = (category: any, dimensionId: string, signalClassification: any) => {
    if (!signalClassification?.dimensions?.[dimensionId]?.categories?.[category.id]?.labels) {
      return 0;
    }
    // Count how many labels in this category are detected for the signal
    const labels = signalClassification.dimensions[dimensionId].categories[category.id].labels;
    return Object.keys(labels).length;
  };

  const getDimensionSignalCountForSignal = (dimensionId: string, signalClassification: any) => {
    if (!signalClassification?.dimensions?.[dimensionId]) {
      return 0;
    }
    // Count total labels across all categories in this dimension
    let count = 0;
    const dimension = signalClassification.dimensions[dimensionId];
    if (dimension.categories) {
      Object.values(dimension.categories).forEach((category: any) => {
        if (category.labels) {
          count += Object.keys(category.labels).length;
        }
      });
    }
    return count;
  };

  if (loading) {
    return <TaxonomyLoadingState />;
  }

  return (
    <div className="flex min-h-0 w-full flex-col gap-4 xl:flex-row">
      <Card className="min-h-[42rem] min-w-0 flex-1 overflow-hidden border-border/80 shadow-[var(--shadow-card)]">
        <div className="flex h-full min-h-0 flex-col">
          <div className="flex items-start justify-between gap-4 border-b border-border/70 bg-background/90 px-4 py-3 backdrop-blur">
            <div className="space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary">{modeLabel}</Badge>
                <Badge variant="outline">
                  {nodes.length} nodes
                </Badge>
                {filterCategory && <Badge variant="outline">Filter: {filterCategory}</Badge>}
              </div>
              <p className="text-[13px] leading-[1.7] text-muted-foreground">
                Pan and zoom the graph to inspect the taxonomy map. Select a label to open the affected signals list.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {highlightSignal && (
                <Button variant="outline" size="sm" onClick={() => window.history.back()}>
                  Back to signal
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={loadTaxonomyData}>
                Refresh data
              </Button>
            </div>
          </div>

          <div className="relative min-h-[36rem] flex-1 bg-[radial-gradient(circle_at_top_left,rgba(67,66,245,0.08),transparent_32%),linear-gradient(180deg,rgba(251,249,246,0.95),rgba(251,249,246,0.88))]">
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onNodeClick={handleNodeClick}
              nodeTypes={nodeTypes}
              fitView
              attributionPosition="bottom-left"
              className="h-full w-full"
            >
              <Background color="rgba(67, 66, 245, 0.12)" gap={18} variant={BackgroundVariant.Dots} />
              <Controls />
              <MiniMap
                nodeColor={(node) => {
                  if (node.data.type === 'root') return '#2b2b3a';
                  if (node.data.type === 'dimension') return '#4342F5';
                  if (node.data.type === 'category') return '#924EFF';
                  return node.data.detected ? '#92204D' : '#07A0C3';
                }}
                pannable
                zoomable
              />
            </ReactFlow>
          </div>
        </div>
      </Card>

      <Card className="min-h-[42rem] w-full overflow-hidden border-border/80 shadow-[var(--shadow-card)] xl:w-[22rem]">
        <div className="flex h-full min-h-0 flex-col">
          <div className="border-b border-border/70 px-4 py-3">
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1">
                <h2 className="font-jakarta text-[15px] font-bold tracking-[-0.01em] text-foreground">
                  {selectedLabelForSignals ? 'Affected signals' : 'Taxonomy overview'}
                </h2>
                <p className="text-[13px] leading-[1.7] text-muted-foreground">
                  {selectedLabelForSignals
                    ? `Signals carrying ${selectedLabelForSignals.name}.`
                    : 'Coverage, confidence, and detection distribution.'}
                </p>
              </div>

              {selectedLabelForSignals && (
                <Button variant="ghost" size="icon" onClick={() => setSelectedLabelForSignals(null)} aria-label="Close signal list">
                  <span className="text-lg leading-none">×</span>
                </Button>
              )}
            </div>
          </div>

          <div className="min-h-0 flex-1">
            <ScrollArea className="h-full">
              {selectedLabelForSignals ? (
                <TaxonomySignalsList
                  labelId={selectedLabelForSignals.id}
                  labelName={selectedLabelForSignals.name}
                  onSignalClick={(signalId) => {
                    window.location.href = `/v3/vault/${signalId}`;
                  }}
                />
              ) : (
                <TaxonomyStatsPanel
                  stats={stats}
                  selectedNodeId={selectedNode}
                  nodes={nodes}
                  taxonomyData={taxonomyData}
                  signalClassification={signalClassification}
                  highlightSignal={highlightSignal}
                />
              )}
            </ScrollArea>
          </div>
        </div>
      </Card>
    </div>
  );
};

const TaxonomyVisualizer: React.FC<TaxonomyVisualizerProps> = (props) => {
  return (
    <ReactFlowProvider>
      <TaxonomyVisualizerContent {...props} />
    </ReactFlowProvider>
  );
};

export default TaxonomyVisualizer;
